// src/pages/FamilySpace.tsx
// Page MixMind : Espace famille & school (gestion des profils enfants/élèves pour parents/enseignants).
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Trash2, Plus, Edit3 } from "lucide-react";
import type { FamilyProfile, AvatarKind } from "../data/fakeUsers";
import type { ProfileRole } from "../types/profiles";
import { computeAgeBand } from "../types/profiles";
import ProfileForm, { ProfileFormValues } from "../components/account/ProfileForm";

type ProfileFormState = {
  displayName: string;
  role: ProfileRole;
  age?: number | null;
  avatar?: AvatarKind;
};

export default function FamilySpace() {
  const { account, isFamily, isSchool, isIndividual, addFamilyProfile, removeFamilyProfile, updateFamilyProfile } =
    useAuth();

  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<FamilyProfile | null>(null);
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    displayName: "",
    role: "enfant",
    age: undefined,
    avatar: "initial",
  });
  const [formError, setFormError] = useState<string | null>(null);

  const profiles = useMemo(() => {
    if (!account) return [];
    if (account.type === "family" || account.type === "school") {
      return account.profiles || [];
    }
    return [];
  }, [account]);

  function roleToLabel(role: FamilyProfile["role"]) {
    if (role === "parent") return "Parent";
    if (role === "child" || role === "enfant") return "Enfant";
    if (role === "teacher" || role === "enseignant") return "Enseignant";
    if (role === "student" || role === "eleve") return "Élève";
    return "Profil";
  }

  function normalizeInputRole(role: ProfileRole): FamilyProfile["role"] {
    if (role === "enfant") return "child";
    if (role === "eleve") return "student";
    if (role === "enseignant") return "teacher";
    return "parent";
  }

  function avatarDisplay(label?: string, avatar?: AvatarKind | null) {
    if (avatar && avatar !== "initial") {
      if (avatar === "boy") return "👦";
      if (avatar === "girl") return "👧";
      if (avatar === "man") return "👨";
      if (avatar === "woman") return "👩";
    }
    const name = label?.trim();
    if (name) return name.charAt(0).toUpperCase();
    return "?";
  }

  function openCreateProfile() {
    setEditingProfile(null);
    setProfileForm({
      displayName: "",
      role: isSchool ? "eleve" : "enfant",
      age: undefined,
      avatar: "initial",
    });
    setIsProfileModalOpen(true);
    setActionMessage(null);
    setFormError(null);
  }

  function openEditProfile(profile: FamilyProfile) {
    const currentRole: ProfileRole =
      profile.role === "child"
        ? "enfant"
        : profile.role === "student"
        ? "eleve"
        : profile.role === "teacher"
        ? "enseignant"
        : "parent";

    setEditingProfile(profile);
    setProfileForm({
      displayName: profile.displayName || profile.firstName || "",
      role: currentRole,
      age: profile.age ?? undefined,
      avatar: (profile.avatar as AvatarKind) || "initial",
    });
    setIsProfileModalOpen(true);
    setActionMessage(null);
    setFormError(null);
  }

  function handleSaveProfile(values: ProfileFormValues) {
    if (!values.displayName.trim()) {
      setFormError("Merci d'indiquer un prénom ou un pseudo.");
      return;
    }
    if (values.age !== undefined && values.age !== null) {
      if (values.age < 3 || values.age > 120) {
        setFormError("Merci d'indiquer un âge compris entre 3 et 120 ans.");
        return;
      }
    }

    const resolvedRole = normalizeInputRole(values.role);
    const ageValue = values.age === undefined ? null : values.age ?? null;
    const ageBand = computeAgeBand(ageValue ?? undefined);
    const payload = {
      firstName: values.displayName.trim(),
      displayName: values.displayName.trim(),
      role: resolvedRole,
      age: ageValue,
      ageBand,
      avatar: values.avatar || "initial",
    };

    if (editingProfile) {
      updateFamilyProfile(editingProfile.id, payload);
      setActionMessage("Profil mis à jour (démonstration).");
    } else {
      const result = addFamilyProfile(payload);
      if (!result.ok && result.reason) {
        setFormError("Impossible d'ajouter un profil (limites de la démo).");
        return;
      }
      setActionMessage("Profil ajouté (démonstration).");
    }

    setIsProfileModalOpen(false);
    setEditingProfile(null);
    setProfileForm({
      displayName: "",
      role: isSchool ? "eleve" : "enfant",
      age: undefined,
      avatar: "initial",
    });
    setFormError(null);
  }

  function handleRemoveProfile(id: string) {
    removeFamilyProfile(id);
    setActionMessage("Profil supprimé (démonstration).");
  }

  function handleCloseForm() {
    setIsProfileModalOpen(false);
    setEditingProfile(null);
    setFormError(null);
    setActionMessage(null);
  }

  const accountType = account?.type;
  const isFamilyMode = isFamily;
  const isSchoolMode = isSchool;

  if (isIndividual || accountType === "individual") {
    return (
      <div className="max-w-4xl mx-auto flex flex-col gap-6 py-12">
        <div className="rounded-2xl border p-6 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700 space-y-3 text-center">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
            Espace réservé aux comptes Famille ou School
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Cette section est conçue pour les comptes multi-profils. Votre compte individuel peut continuer à profiter de
            MixMind, et vous pourrez découvrir les options Famille ou School depuis votre page de compte.
          </p>
          <div className="flex justify-center">
            <Link
              to="/account"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              Découvrir les comptes Famille / School
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const headerTitle = isSchoolMode ? "Espace school" : "Espace famille";
  const introText = isSchoolMode
    ? "Espace dédié aux enseignants pour gérer les classes, les profils élèves et les thèmes partagés. Certaines fonctionnalités sont encore en préparation."
    : "Espace dédié aux parents pour créer et gérer les profils enfants, suivre leurs activités et choisir des thèmes adaptés. Certaines fonctionnalités sont encore en préparation.";
  const profileSectionTitle = isSchoolMode ? "Profils élèves / enseignants" : "Profils enfants / parents";
  const profileSectionDescription = isSchoolMode
    ? "Consultez et gérez les profils enseignants ou élèves de votre établissement. Certaines limites restent liées au mode démo."
    : "Consultez et gérez les profils enfants de la famille. Certaines limites restent liées au mode démo.";
  const organizationTitle = isSchoolMode ? "Organisation des classes" : "Organisation familiale";
  const organizationDescription = isSchoolMode
    ? "Cette section servira à structurer les classes et groupes d'élèves, avec des outils pour gérer plusieurs apprenants en même temps."
    : "Cette section servira à regrouper les profils de la famille et faciliter la gestion de plusieurs enfants.";

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-6 py-8">
      {/* En-tête */}
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">{headerTitle}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-3xl">{introText}</p>
      </header>

      {/* Présentation contextualisée */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">
          {isSchoolMode ? "Mode school" : "Mode famille"}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {isSchoolMode
            ? "Les équipes pédagogiques peuvent créer des classes, associer des élèves et sélectionner des thèmes ou exercices pour un groupe."
            : "Les parents peuvent créer des profils enfants, suivre leurs activités et choisir des thèmes adaptés à leur âge pour nourrir leur curiosité."}
        </p>
      </section>

      {/* Profils enfants / élèves */}
      {/* TODO MixMind: plus tard, filtrer les contenus/accès selon rôle et ageBand du profil. */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">{profileSectionTitle}</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">{profileSectionDescription}</p>

        <div className="mt-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={openCreateProfile}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium border-slate-300 text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              <Plus className="w-3 h-3" />
              <span>+ Ajouter un profil</span>
            </button>
            {actionMessage && (
              <span className="text-[11px] text-slate-500 dark:text-slate-300">{actionMessage}</span>
            )}
          </div>

          <div className="flex flex-col gap-3">
            {profiles.map((p) => {
              const labelName = p.displayName || p.firstName;
              const ageLabel = p.age != null ? `Âge : ${p.age} ans` : null;
              const bandLabel = p.ageBand ? `Tranche : ${p.ageBand}` : null;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-xl border p-3 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-semibold text-slate-700 dark:text-slate-200">
                      {avatarDisplay(labelName, p.avatar)}
                    </div>
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{labelName}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{roleToLabel(p.role)}</p>
                      {ageLabel && <p className="text-[11px] text-slate-500 dark:text-slate-400">{ageLabel}</p>}
                      {bandLabel && <p className="text-[11px] text-slate-500 dark:text-slate-400">{bandLabel}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 text-[11px]">
                    <button
                      type="button"
                      onClick={() => openEditProfile(p)}
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-1 border-slate-300 text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
                    >
                      <Edit3 className="w-3 h-3" />
                      <span>Modifier</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveProfile(p.id)}
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-1 border-red-300 text-red-600 bg-red-50 hover:bg-red-100 dark:border-red-500/60 dark:text-red-100 dark:bg-red-900/40"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              );
            })}

            {profiles.length === 0 && (
              <div className="rounded-2xl border p-3 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-300">
                Aucun profil pour le moment. Ajoutez un profil pour commencer.
              </div>
            )}
          </div>
        </div>

        {isProfileModalOpen && (
          <section className="mt-4 rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700 max-w-md">
            <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-50">
              {editingProfile ? "Modifier un profil" : "Ajouter un profil"}
            </h3>
            <ProfileForm
              initialValues={profileForm}
              onSubmit={handleSaveProfile}
              onCancel={handleCloseForm}
              submitLabel="Enregistrer"
              errorText={formError}
            />
          </section>
        )}
      </section>

      {/* Organisation contextualisée */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">{organizationTitle}</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">{organizationDescription}</p>
      </section>

      {/* Conseils d'utilisation */}
      <section className="rounded-2xl border p-4 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Conseils d&apos;utilisation</h2>
        <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
          <li>
            • Commencez par un thème simple pour observer comment {isSchoolMode ? "la classe" : "l’enfant"} réagit.
          </li>
          <li>• Privilégiez des sessions courtes mais régulières.</li>
          <li>• Prenez quelques minutes pour discuter après chaque activité : ce qui a été compris, ressenti, observé.</li>
          <li>• Adaptez les thèmes et la difficulté à l’âge et au niveau de chacun.</li>
        </ul>
      </section>
    </div>
  );
}
